import React, { useContext, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import AvatarWithTooltip from './AvatarWithTooltip';
import AvatarDisplay from './AvatarDisplay';

const LeftSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, ensName, address, logout, getAuthHeaders } = useContext(AuthContext);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Poll for unread notifications every minute
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchUnread = async () => {
      try {
        const headers = getAuthHeaders();
        const res = await fetch('/api/zpost/notifications/unread-count', { headers });
        const data = await res.json();
        if (data.success) setUnreadCount(data.count);
      } catch (err) {
        console.error('Failed to fetch unread count', err);
      }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, getAuthHeaders]);

  const navItems = [
    { path: '/', icon: 'fas fa-home', label: 'Home', section: 'primary' },
    {
      path: isAuthenticated ? `/zhat?ens=${encodeURIComponent(ensName)}` : '/zhat',
      icon: 'fas fa-comments',
      label: 'Zhat',
      external: true,
      target: '_blank',
      rel: 'noopener noreferrer',
      section: 'core'
    },
    { path: '/zearch', icon: 'fas fa-search', label: 'Zearch', section: 'core' },
    { path: '/home/wallet', icon: 'fas fa-wallet', label: 'Zwallet', section: 'core' },
    { path: '/home/identity', icon: 'fas fa-id-card', label: 'Identity', section: 'core' },
    {
      path: 'https://banksystem.co/',
      icon: 'fas fa-university',
      label: 'BankSystem',
      external: true,
      target: '_blank',
      rel: 'noopener noreferrer',
      section: 'external'
    },
    // Notices – replaces Notifications
    { path: '/notices', icon: 'fas fa-bell', label: 'Notices', section: 'core', requiresAuth: true },
    // Settings → /help
    { path: '/help', icon: 'fas fa-cog', label: 'Settings', section: 'core', requiresAuth: true },
  ];
  
  const handleZhatClick = (e, ensName) => {
    if (ensName) {
      e.preventDefault();
      window.open(`/zhat?ens=${encodeURIComponent(ensName)}`, '_blank');
    } else {
      e.preventDefault();
      window.open('/zhat', '_blank');
    }
  };
  
  // Group items by section
  const primaryItems = navItems.filter(item => item.section === 'primary');
  const coreItems = navItems.filter(item => item.section === 'core');
  const externalItems = navItems.filter(item => item.section === 'external');
  
  const handleProfileClick = (e) => {
    e.preventDefault();
    console.log('Profile clicked, navigating to /profile');
    navigate('/profile');
  };

  return (
    <div className="sidebar-nav-container">
      {/* User Menu Dropdown */}
      {isAuthenticated && showUserMenu && (
        <div style={{
          position: 'absolute',
          top: '60px',
          left: '1rem',
          right: '1rem',
          background: 'var(--card-bg)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          padding: '1rem',
          zIndex: 100,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.75rem',
            marginBottom: '1rem',
            paddingBottom: '1rem',
            borderBottom: '1px solid var(--border)'
          }}>
            <AvatarDisplay 
              ensName={ensName}
              address={address}
              size={48}
            />
            <div style={{ flex: 1 }}>
              <div style={{ 
                color: 'var(--gold)', 
                fontWeight: 'bold',
                fontSize: '0.95rem'
              }}>
                {ensName?.replace('.zpost.eth', '')}
              </div>
              <div style={{ 
                color: 'var(--text-secondary)', 
                fontSize: '0.8rem',
                wordBreak: 'break-all'
              }}>
                {address?.slice(0, 10)}...{address?.slice(-8)}
              </div>
            </div>
          </div>
          
          <button
            onClick={() => {
              setShowUserMenu(false);
              window.location.reload();
            }}
            className="sidebar-nav-item"
            style={{ 
              width: '100%', 
              marginBottom: '0.5rem',
              background: 'transparent',
              border: 'none',
              textAlign: 'left'
            }}
          >
            <i className="fas fa-sync-alt"></i>
            <span>Refresh</span>
          </button>
          
          <button
            onClick={() => {
              logout();
              setShowUserMenu(false);
            }}
            className="sidebar-nav-item"
            style={{ 
              width: '100%',
              background: 'transparent',
              border: 'none',
              textAlign: 'left',
              color: '#ff4444'
            }}
          >
            <i className="fas fa-sign-out-alt"></i>
            <span>Disconnect</span>
          </button>
        </div>
      )}
      
      {/* Primary Navigation - HOME ONLY */}
      <div className="sidebar-nav-primary">
        {primaryItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
            >
              <i className={item.icon}></i>
              <span>{item.label}</span>
            </Link>
          );
        })}
      </div>
      
      {/* Core Apps Navigation */}
      <div className="sidebar-nav-middle">
        {coreItems.map((item) => {
          if (item.external) {
            if (item.label === 'Zhat') {
              return (
                <a
                  key={item.path}
                  href={item.path}
                  target={item.target}
                  rel={item.rel}
                  className="sidebar-nav-item"
                  onClick={(e) => handleZhatClick(e, ensName)}
                >
                  <i className={item.icon}></i>
                  <span>{item.label}</span>
                  <span style={{
                    marginLeft: 'auto',
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)',
                    display: isAuthenticated ? 'block' : 'none'
                  }}>
                    <i className="fas fa-external-link-alt"></i>
                  </span>
                </a>
              );
            }
            return null;
          }

          if (item.requiresAuth && !isAuthenticated) return null;

          const isActive = location.pathname === item.path;

          // Special handling for Notices with unread badge
          if (item.path === '/notices') {
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
              >
                <i className={item.icon}></i>
                <span>{item.label}</span>
                {unreadCount > 0 && (
                  <span style={{
                    marginLeft: 'auto',
                    background: 'var(--gold)',
                    color: '#000',
                    borderRadius: '10px',
                    padding: '2px 8px',
                    fontSize: '0.8rem'
                  }}>
                    {unreadCount}
                  </span>
                )}
              </Link>
            );
          }

          // Default rendering for other core items
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
            >
              <i className={item.icon}></i>
              <span>{item.label}</span>
            </Link>
          );
        })}

        {/* Single Profile Link (hardcoded) */}
        <a
          href="/profile"
          onClick={handleProfileClick}
          className="sidebar-nav-item"
          style={{ textDecoration: 'none', cursor: 'pointer' }}
        >
          <i className="fas fa-user"></i>
          <span>Profile</span>
        </a>
      </div>
      
      {/* External Services - BankSystem */}
      <div className="sidebar-nav-lower">
        {externalItems.map((item) => {
          if (item.external) {
            return (
              <a
                key={item.path}
                href={item.path}
                target={item.target}
                rel={item.rel}
                className="sidebar-nav-item"
              >
                <i className={item.icon}></i>
                <span>{item.label}</span>
                <span style={{
                  marginLeft: 'auto',
                  fontSize: '0.75rem',
                  color: 'var(--text-secondary)'
                }}>
                  <i className="fas fa-external-link-alt"></i>
                </span>
              </a>
            );
          }
          return null;
        })}
      </div>
      
      {/* User Profile Section */}
      <div className="sidebar-profile-section">
        {isAuthenticated ? (
          <div className="profile-card">
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.75rem',
              marginBottom: '1rem'
            }}>
              <Link
                to="/profile"
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.75rem',
                  flex: 1,
                  textDecoration: 'none',
                  color: 'inherit'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <AvatarWithTooltip
                  ensName={ensName}
                  address={address}
                  size={40}
                >
                  <AvatarDisplay 
                    ensName={ensName}
                    address={address}
                    size={40}
                  />
                </AvatarWithTooltip>
                
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p className="profile-ens" style={{ 
                    margin: 0, 
                    color: 'var(--gold)',
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {ensName?.replace('.zpost.eth', '')}
                  </p>
                  <p className="profile-domain" style={{ 
                    margin: 0, 
                    color: 'var(--text-secondary)',
                    fontSize: '0.8rem'
                  }}>
                    {ensName}
                  </p>
                </div>
              </Link>
              
              <i 
                className="fas fa-chevron-down" 
                style={{ 
                  color: 'var(--text-secondary)',
                  fontSize: '0.8rem',
                  cursor: 'pointer'
                }}
                onClick={() => setShowUserMenu(!showUserMenu)}
              ></i>
            </div>
            
            <p className="profile-tagline" style={{ 
              margin: 0, 
              fontSize: '0.85rem',
              color: 'var(--text-secondary)'
            }}>
              Your sovereign identity
            </p>
            
            <div style={{ 
              marginTop: '1rem',
              fontSize: '0.8rem',
              color: 'var(--text-secondary)',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.25rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <i className="fas fa-comments" style={{ color: 'var(--gold)' }}></i>
                <span>Click "Zhat" to message</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <i className="fas fa-search" style={{ color: 'var(--gold)' }}></i>
                <span>Click "Zearch" to search</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <i className="fas fa-id-card" style={{ color: 'var(--gold)' }}></i>
                <span>Click "Identity" for .zpost.eth</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="profile-card">
            <p style={{ 
              color: 'var(--text-secondary)',
              marginBottom: '1rem',
              fontSize: '0.9rem'
            }}>
              Connect your wallet to access Zpost
            </p>
            <button 
              onClick={() => window.location.href = '/'}
              style={{
                background: 'var(--gold)',
                color: 'var(--black)',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '9999px',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '0.8rem',
                width: '100%'
              }}
            >
              Get Started
            </button>
            <div style={{ 
              marginTop: '1rem',
              fontSize: '0.8rem',
              color: 'var(--text-secondary)',
              textAlign: 'center'
            }}>
              <i className="fas fa-external-link-alt"></i> Zhat opens in new tab
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeftSidebar;
